import { InformationChip } from '@europrocurement/flexy-components';
import {
    FACTURES_OPEN_SEARCH_SLICE_NAME,
    useFacturationService,
} from '@europrocurement/l2d-domain';
import { FactureOpenSearchOpensearchFactureReadDossiersInner } from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { AnyAction } from '@reduxjs/toolkit';
import { Box, Typography, useTheme } from '@mui/material';
import React from 'react';
import { useDispatch } from 'react-redux';

const useFactureColumnsRenderFunctions = () => {
    const { palette } = useTheme();
    const dispatch = useDispatch();
    const { getFactureOpenSearch } = useFacturationService();

    /**
     * onClick on the collection icon/label -> dispatch the collection id as slice filter and reload redux state
     * @param e React.MouseEvent<HTMLElement>
     * @param collectionId number
     */
    const filterInvoicesByDossierCollectionId = (
        e: React.MouseEvent<HTMLElement>,
        collectionId: number,
    ) => {
        e.preventDefault();
        e.stopPropagation();

        dispatch({
            type: `${FACTURES_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
            payload: {
                key: 'collectionId',
                value: collectionId,
            },
        });

        dispatch(getFactureOpenSearch({}) as unknown as AnyAction);
    };

    // Start Functions to build columns cells
    const referenceDossierColumnRender = (
        dossiers: Array<FactureOpenSearchOpensearchFactureReadDossiersInner>,
        reference?: string | null,
    ) => {
        if (dossiers.length > 1) {
            return (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris50"
                >
                    Facture multiple
                </Typography>
            );
        }

        let displayedValue: string | JSX.Element = '';
        if (dossiers.length === 1) {
            // Dossier numero
            displayedValue = `#${dossiers[0].dossierNumero}`;
            // or dossier collection command number if set
            if (dossiers[0].collectionNumero && dossiers[0].collectionId) {
                displayedValue = (
                    <Box
                        sx={{ display: 'flex', alignItems: 'center' }}
                        onClick={(e) =>
                            filterInvoicesByDossierCollectionId(
                                e,
                                dossiers[0].collectionId as number,
                            )
                        }
                    >
                        <FaOptionIcon icon={['fad', 'link-horizontal']} />
                        {`#${dossiers[0].collectionNumero}`}
                    </Box>
                );
            }
        }

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {reference}
                </Typography>

                <Typography
                    variant="l2dtableCell"
                    color="text.gris50"
                >
                    {displayedValue}
                </Typography>
            </Box>
        );
    };

    const societeOpereeColumnRender = (
        dossiers: Array<FactureOpenSearchOpensearchFactureReadDossiersInner>,
    ) => {
        // check if the dossiers linked to the bill have different clients ids
        const clientIds = [...new Set(dossiers.map((dossier) => dossier.clientId))];

        if (clientIds.length > 1) {
            return (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    Societés multiple
                </Typography>
            );
        }
        return (
            <Typography
                variant="l2dtableCell"
                color="text.gris70"
            >
                {dossiers.length > 0 && dossiers[0].raisonSociale}
            </Typography>
        );
    };

    const statusColumnRender = function (status?: string | null) {
        let statusText = "date d'échéance manquante";
        let color: 'error' | 'success' | 'primary' | 'secondary' | 'warning' = 'error';
        switch (status) {
            case 'PAYED':
                statusText = 'réglée';
                color = 'success';
                break;
            case 'TO_PAY':
                statusText = 'à régler';
                color = 'warning';
                break;
            case 'LATE':
                statusText = 'en retard';
                color = 'error';
                break;

            default:
                break;
        }

        const baseColor = palette[color].main;

        return (
            <InformationChip
                label={
                    <Typography
                        variant="l2dtableCell"
                        color={baseColor}
                    >
                        {statusText}
                    </Typography>
                }
                color={color}
            />
        );
    };

    return {
        referenceDossierColumnRender,
        societeOpereeColumnRender,
        statusColumnRender,
    };
};

export default useFactureColumnsRenderFunctions;
